import React, { useMemo, useState, useEffect, useRef } from "react";
import { Paper, Box, Typography, Tooltip, Alert } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme } from "@mui/material/styles";

import { useAuthContext } from "../../../context/AuthContext";
import { useProductApplications } from "../../../hooks/useProducts";
import { useCategories } from "../../../hooks/useCategories";
import { useCompanies } from "../../../hooks/useCompanies";
import { useS3Upload } from "../../../hooks/useS3Upload";
import { useTranslation } from "react-i18next";

/* Sections */
import ContentSection from "./sections/ContentSection";
import PriceStockSection from "./sections/PriceStockSection";
import InstallmentsSection from "./sections/InstallmentsSection";
import DescriptionTechSection from "./sections/DescriptionTechSection";
import IncludedSection from "./sections/IncludedSection";
import ApplicationSection from "./sections/ApplicationSection";
import CompatibilitySection from "./sections/CompatibilitySection";
import WarrantySection from "./sections/WarrantySection";
import ReturnsSection from "./sections/ReturnsSection";
import PaymentSection from "./sections/PaymentSection";
import GallerySection from "./sections/GallerySection";
import StatusHighlightSection from "./sections/StatusHighlightSection";
import ActionBar from "./sections/ActionBar";
import RelatedProductsSection from "./sections/RelatedProductsSection";

/* ========================= Helpers ========================= */
const genId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const MAX_FILE_MB = 10;

const appLabelToType = (label) => {
  const t = (label || "").toLowerCase();
  if (/ônibus|onibus|bus/i.test(t)) return "bus";
  if (/caminh|truck/i.test(t)) return "truck";
  return t
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

const typeToAppLabel = (type, apps = []) => {
  const t = String(type || "").trim();
  if (!t) return t;
  const found = (apps || []).find((label) => appLabelToType(label) === t);
  return found || t;
};

const groupCompatByType = (arr = [], apps = []) => {
  const out = {};
  (arr || []).forEach((it) => {
    if (it && typeof it === "object" && (it.family || it.type)) {
      const key = typeToAppLabel(it.type || "Outros", apps);
      const rec = {
        family: String(it.family || "").trim(),
        years: it.years ? String(it.years).trim() : undefined,
        engine: it.engine ? String(it.engine).trim() : undefined,
        notes: it.notes ? String(it.notes).trim() : undefined,
      };
      (out[key] ||= []).push(rec);
    }
  });
  return out;
};

export default function AddProductForm({
  product,
  onClose,
  onSubmit,
  isFetching,
  allProductsForProject = [],
  defaultProjectId = null,
}) {
  const theme = useTheme();
  const { userData } = useAuthContext();
  const { t } = useTranslation("products");
  const fileInputRef = useRef(null);

  const [errorMessage, setErrorMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const getDefaultIdsProjetos = () => {
    if (product?.ids_projetos != null) return product.ids_projetos;
    if (defaultProjectId != null) return defaultProjectId;
    return null;
  };

  /* Categorias */
  const {
    rootCategories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
    error: categoriesErrorObj,
  } = useCategories({ page: 1, limit: 500 });

  /* Projetos via Companies */
  const {
    data: companiesQuery = { companies: [], pagination: { total: 0 } },
    isLoading: companiesLoading,
    isError: companiesError,
    error: companiesErrorObj,
  } = useCompanies(1, 500, "");

  const companies = companiesQuery?.companies || [];

  /* Compat do produto */
  const compatFromProduct =
    product?.compatibility?.applications && Array.isArray(product.compatibility.applications)
      ? groupCompatByType(product.compatibility.applications, product?.aplicacao || [])
      : {};

  /* --------- FORM STATE --------- */
  const [formData, setFormData] = useState(() => ({
    ...product,
    ids_projetos: getDefaultIdsProjetos(),
    tipo: product?.tipo ?? 1,
    id_categoria: product?.id_categoria ?? null,
    id_subcategoria: product?.id_subcategoria ?? null,
    nome: product?.nome ?? "",
    sku: product?.sku ?? "",
    // TAGS
    tags: product?.tags ?? "",

    preco: isFinite(+product?.preco) ? +product.preco : "",
    preco_comdesconto: isFinite(+product?.preco_comdesconto) ? +product.preco_comdesconto : "",
    estoque: isFinite(+product?.estoque) ? +product.estoque : "",
    peso: isFinite(+product?.peso) ? +product.peso : "",
    largura: isFinite(+product?.largura) ? +product.largura : "",
    altura: isFinite(+product?.altura) ? +product.altura : "",
    comprimento: isFinite(+product?.comprimento) ? +product.comprimento : "",
    detalhes: product?.detalhes ?? "",
    status: product?.status == null ? 1 : Number(product.status),
    destaque: product?.destaque ? 1 : 0,

    parcelamento_semjuros_enabled: product?.parcelamento_semjuros_enabled ? 1 : 0,
    parcelamento_semjuros_qtd: isFinite(+product?.parcelamento_semjuros_qtd)
      ? +product.parcelamento_semjuros_qtd
      : "",
    parcelamento_comjuros_enabled: product?.parcelamento_comjuros_enabled ? 1 : 0,
    parcelamento_comjuros_qtd: isFinite(+product?.parcelamento_comjuros_qtd)
      ? +product.parcelamento_comjuros_qtd
      : "",
    parcelamento_comjuros_taxa_mes: isFinite(+product?.parcelamento_comjuros_taxa_mes)
      ? +product.parcelamento_comjuros_taxa_mes
      : "",

    caracteristicas: Array.isArray(product?.caracteristicas) ? [...product.caracteristicas] : [],
    ficha_tecnica: Array.isArray(product?.ficha_tecnica) ? [...product.ficha_tecnica] : [],

    aplicacao: Array.isArray(product?.aplicacao)
      ? [...product.aplicacao]
      : Array.isArray(product?.aplicacao_itens)
      ? [...product.aplicacao_itens]
      : [],

    inclusos_itens: Array.isArray(product?.inclusos_itens) ? [...product.inclusos_itens] : [],
    inclusos_dica: product?.inclusos_dica ?? "",

    compat_alerta: product?.compat_alerta ?? "",
    compat_aplicacoes: Array.isArray(product?.compat_aplicacoes) ? [...product.compat_aplicacoes] : [],
    compat_models: product?.compat_models ?? compatFromProduct ?? {},

    garantia_alerta: product?.garantia_alerta ?? "",
    garantia_descricao: product?.garantia_descricao ?? "",
    garantia_acoes: (product?.garantia_acoes || []).map((s) => String(s || "").trim()).filter(Boolean),
    garantia_exclusoes: (product?.garantia_exclusoes || []).map((s) => String(s || "").trim()).filter(Boolean),
    garantia_policy_url: (product?.garantia_policy_url || "").trim() || null,

    /* Trocas & Devoluções */
    trocas_alerta: product?.trocas_alerta ?? "",
    trocas_descricao: product?.trocas_descricao ?? "",
    trocas_col_esq: product?.trocas_col_esq ?? "",
    trocas_col_dir: product?.trocas_col_dir ?? "",
    trocas_itens: Array.isArray(product?.trocas_itens) ? [...product.trocas_itens] : [],
    trocas_passos: Array.isArray(product?.trocas_passos) ? [...product.trocas_passos] : [],

    pagamento_regras: product?.pagamento_regras ?? "",

    galeria: Array.isArray(product?.galeria)
      ? [...product.galeria].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
      : product?.imagem
      ? [{ id: genId(), url: product.imagem, ordem: 1, isCover: true }]
      : [],
    id_usuario: product?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,

    // SKUs relacionados (cross-sell manual) – string ou array vinda da API
    related_skus: Array.isArray(product?.related_skus)
      ? product.related_skus.join(", ")
      : (product?.related_skus || ""),
  }));

  /* Upload S3 */
  const { uploadMany: uploadManyReal } = useS3Upload();

  /* Sugestões de aplicações */
  const { data: appsOptionsRaw = [], isLoading: appsLoading } = useProductApplications("", 200, true);
  const appsLabels = useMemo(() => {
    const list = (Array.isArray(appsOptionsRaw) ? appsOptionsRaw : [])
      .map((o) => (typeof o === "string" ? o : (o?.label || o?.key || "")))
      .map((s) => String(s || "").trim())
      .filter(Boolean);
    return Array.from(new Set(list));
  }, [appsOptionsRaw]);

  /* Atualiza quando trocar produto (apenas se tiver ID -> edição) */
  useEffect(() => {
    if (!product?.id) return;
    setFormData((prev) => ({
      ...prev,
      ...product,
      ids_projetos: product?.ids_projetos ?? null,
      tipo: product?.tipo ?? 1,
      id_categoria: product?.id_categoria ?? null,
      id_subcategoria: product?.id_subcategoria ?? null,
      nome: product?.nome ?? "",
      sku: product?.sku ?? "",
      // TAGS
      tags: product?.tags ?? "",

      preco: isFinite(+product?.preco) ? +product.preco : "",
      preco_comdesconto: isFinite(+product?.preco_comdesconto) ? +product.preco_comdesconto : "",
      estoque: isFinite(+product?.estoque) ? +product.estoque : "",
      peso: isFinite(+product?.peso) ? +product.peso : "",
      largura: isFinite(+product?.largura) ? +product.largura : "",
      altura: isFinite(+product?.altura) ? +product.altura : "",
      comprimento: isFinite(+product?.comprimento) ? +product.comprimento : "",
      detalhes: product?.detalhes ?? "",
      status: product?.status == null ? 1 : Number(product.status),
      destaque: product?.destaque ? 1 : 0,

      parcelamento_semjuros_enabled: product?.parcelamento_semjuros_enabled ? 1 : 0,
      parcelamento_semjuros_qtd: isFinite(+product?.parcelamento_semjuros_qtd)
        ? +product.parcelamento_semjuros_qtd
        : "",
      parcelamento_comjuros_enabled: product?.parcelamento_comjuros_enabled ? 1 : 0,
      parcelamento_comjuros_qtd: isFinite(+product?.parcelamento_comjuros_qtd)
        ? +product.parcelamento_comjuros_qtd
        : "",
      parcelamento_comjuros_taxa_mes: isFinite(+product?.parcelamento_comjuros_taxa_mes)
        ? +product.parcelamento_comjuros_taxa_mes
        : "",

      caracteristicas: Array.isArray(product?.caracteristicas) ? [...product.caracteristicas] : [],
      ficha_tecnica: Array.isArray(product?.ficha_tecnica) ? [...product.ficha_tecnica] : [],

      aplicacao: Array.isArray(product?.aplicacao)
        ? [...product.aplicacao]
        : Array.isArray(product?.aplicacao_itens)
        ? [...product.aplicacao_itens]
        : [],

      inclusos_itens: Array.isArray(product?.inclusos_itens) ? [...product.inclusos_itens] : [],
      inclusos_dica: product?.inclusos_dica ?? "",

      compat_alerta: product?.compat_alerta ?? "",
      compat_aplicacoes: Array.isArray(product?.compat_aplicacoes) ? [...product.compat_aplicacoes] : [],
      compat_models:
        product?.compat_models ??
        (product?.compatibility?.applications
          ? groupCompatByType(product.compatibility.applications, product?.aplicacao || [])
          : {}),

      garantia_alerta: product?.garantia_alerta ?? "",
      garantia_descricao: product?.garantia_descricao ?? "",

      garantia_acoes: (prev.garantia_acoes || []).map((s) => String(s || "").trim()).filter(Boolean),
      garantia_exclusoes: (prev.garantia_exclusoes || []).map((s) => String(s || "").trim()).filter(Boolean),
      garantia_policy_url: (prev.garantia_policy_url || "").trim() || null,

      trocas_alerta: product?.trocas_alerta ?? "",
      trocas_descricao: product?.trocas_descricao ?? "",
      trocas_col_esq: product?.trocas_col_esq ?? "",
      trocas_col_dir: product?.trocas_col_dir ?? "",
      trocas_itens: Array.isArray(product?.trocas_itens) ? [...product.trocas_itens] : [],
      trocas_passos: Array.isArray(product?.trocas_passos) ? [...product.trocas_passos] : [],

      pagamento_regras: product?.pagamento_regras ?? "",

      galeria: Array.isArray(product?.galeria)
        ? [...product.galeria].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
        : product?.imagem
        ? [{ id: genId(), url: product.imagem, ordem: 1, isCover: true }]
        : [],
      id_usuario: product?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,

      related_skus: Array.isArray(product?.related_skus)
        ? product.related_skus.join(", ")
        : (product?.related_skus || ""),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  /* Cleanup previews */
  useEffect(() => {
    return () => {
      formData.galeria?.forEach((img) => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Listener de upload do GallerySection */
  useEffect(() => {
    const onAddFiles = (ev) => {
      const files = ev.detail || [];
      addFiles(files);
    };
    window.addEventListener("gallery:addFiles", onAddFiles);
    return () => window.removeEventListener("gallery:addFiles", onAddFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.galeria]);

  /* ======= Derivados ======= */
  const selectedCategory = useMemo(
    () => rootCategories.find((c) => String(c.id) === String(formData.id_categoria)),
    [rootCategories, formData.id_categoria]
  );
  const subcats = selectedCategory?.children || [];

  const precoBase = Number(formData.preco) || 0;
  const precoFinal =
    formData.preco_comdesconto !== "" && formData.preco_comdesconto !== null
      ? Number(formData.preco_comdesconto)
      : precoBase;

  const economia = precoBase > 0 ? precoBase - precoFinal : 0;
  const economiaPct = precoBase > 0 ? (economia / precoBase) * 100 : 0;

  // agora o preço concessionário pode ser maior ou menor; só invalidamos se for negativo
  const precoConcessionarioValido =
    formData.preco_comdesconto === "" ||
    formData.preco_comdesconto === null ||
    Number(formData.preco_comdesconto) >= 0;

  const handleText = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSwitch = (e) => {
    const { name, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: checked ? 1 : 0 }));
  };

  /* PROJETO */
  const handleProjectChange = (_evt, companyOption) => {
    setFormData((prev) => ({ ...prev, ids_projetos: companyOption ? companyOption.id : null }));
  };

  /* Sincronismo compat_models ⇄ aplicacao */
  useEffect(() => {
    setFormData((prev) => {
      const apps = Array.isArray(prev.aplicacao)
        ? prev.aplicacao.map((s) => String(s || "").trim()).filter(Boolean)
        : [];
      const curr = prev.compat_models || {};
      const next = {};
      apps.forEach((app) => {
        next[app] = Array.isArray(curr[app]) ? curr[app] : [];
      });

      const same =
        Object.keys(next).length === Object.keys(curr).length &&
        Object.keys(next).every((k) => curr[k] === next[k]);
      return same ? prev : { ...prev, compat_models: next };
    });
  }, [formData.aplicacao]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateAndNormalizeFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return [];

    const accepted = [];
    const errors = [];

    for (const f of files) {
      const typeStr = (f && f.type) ? String(f.type) : "";
      const isImage = /^image\//i.test(typeStr);
      const sizeOk = (f && typeof f.size === "number")
        ? f.size <= MAX_FILE_MB * 1024 * 1024
        : false;

      const fileWord = t("form.fileWord");
      if (!isImage) {
        errors.push(
          `${f && f.name ? f.name : fileWord}: ${t("form.fileErrorInvalidType")}`
        );
      } else if (!sizeOk) {
        errors.push(
          `${f && f.name ? f.name : fileWord}: ${t("form.fileErrorTooLarge", { mb: MAX_FILE_MB })}`
        );
      } else {
        accepted.push(f);
      }
    }

    if (errors.length) {
      setErrorMessage(t("form.skippedFiles", { details: errors.join(" ") }));
      setTimeout(() => setErrorMessage(null), 4000);
    }
    return accepted;
  };

  const addFiles = (fileList) => {
    const files = validateAndNormalizeFiles(fileList);
    if (!files.length) return;
    setFormData((prev) => {
      const base = [...(prev.galeria || [])];
      let ordemStart = base.length ? Math.max(...base.map((i) => i.ordem || 0)) + 1 : 1;
      const newImgs = files.map((f, idx) => ({
        id: genId(),
        file: f,
        preview: URL.createObjectURL(f),
        ordem: ordemStart + idx,
        isCover: base.length === 0 && idx === 0 ? true : false,
      }));
      const hasCover = base.some((i) => i.isCover) || newImgs.some((i) => i.isCover);
      if (!hasCover && newImgs.length) newImgs[0].isCover = true;
      return { ...prev, galeria: [...base, ...newImgs] };
    });
  };

  const handleClickAdd = () => fileInputRef.current?.click();

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeImage = (idx) => {
    setFormData((prev) => {
      const list = [...prev.galeria];
      const removed = list.splice(idx, 1)[0];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      if (!list.some((i) => i.isCover) && list[0]) list[0].isCover = true;
      list.forEach((img, i) => (img.ordem = i + 1));
      return { ...prev, galeria: list };
    });
  };

  const moveImage = (idx, dir) => {
    setFormData((prev) => {
      const list = [...prev.galeria];
      const to = idx + dir;
      if (to < 0 || to >= list.length) return prev;
      const [it] = list.splice(idx, 1);
      list.splice(to, 0, it);
      list.forEach((img, i) => (img.ordem = i + 1));
      return { ...prev, galeria: list };
    });
  };

  const makeCover = (idx) => {
    setFormData((prev) => {
      const list = prev.galeria.map((i, iidx) => ({ ...i, isCover: iidx === idx }));
      return { ...prev, galeria: list };
    });
  };

  const buildPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    return `workspace-${workspace}/products/${yyyy}/${mm}`;
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    try {
      setIsUploading(true);
      setUploadProgress({ done: 0, total: 0 });

      const novos = (formData.galeria || []).filter((g) => g.file);
      const uploadedById = new Map();

      if (novos.length) {
        const results = await uploadManyReal(
          novos.map((n) => n.file),
          { prefix: buildPrefix() },
          (done, total) => setUploadProgress({ done, total })
        );

        results.forEach((r, i) => {
          const ref = novos[i];
          if (r?.ok) {
            uploadedById.set(ref.id, r.url);
          } else {
            throw new Error(
              t("form.uploadFailedFor", {
                name: ref.file?.name,
                error: r?.error || t("form.unknownUploadError"),
              })
            );
          }
        });
      }

      const num = (v) => (v === "" ? null : Number(v));
      const galeriaFinal = (formData.galeria || [])
        .map(({ id, url, preview, ordem, isCover }) => {
          const finalUrl = uploadedById.get(id) || url || null;
          return {
            id: id && String(id).startsWith("tmp_") ? undefined : id,
            url: finalUrl || undefined,
            ordem,
            isCover: !!isCover,
          };
        })
        .filter((g) => !!g.url);

      const cover =
        galeriaFinal.find((i) => i.isCover && i.url) ||
        galeriaFinal.find((i) => i.url);

      const compatAplicacoes = Object.entries(formData.compat_models || {}).flatMap(([app, items]) =>
        (items || [])
          .map((m) => ({
            type: appLabelToType(app),
            family: String(m.family || "").trim(),
            years: m.years ? String(m.years).trim() : undefined,
            engine: m.engine ? String(m.engine).trim() : undefined,
            notes: m.notes ? String(m.notes).trim() : undefined,
          }))
          .filter((x) => x.family)
      );

      const payload = {
        id: formData.id,
        ids_projetos: formData.ids_projetos ?? null,
        id_usuario: formData.id_usuario || userData?.id,
        id_usuario_ultima_alteracao: userData?.id,
        tipo: Number(formData.tipo) || 1,
        nome: formData.nome?.trim() || "",
        sku: (formData.sku || "").trim(),

        // TAGS -> string enviada para API
        tags: (formData.tags || "").trim() || null,

        preco: num(formData.preco),
        preco_comdesconto: num(formData.preco_comdesconto),
        estoque: num(formData.estoque),
        peso: num(formData.peso),
        largura: num(formData.largura),
        altura: num(formData.altura),
        comprimento: num(formData.comprimento),
        detalhes: formData.detalhes || "",
        status: Number(formData.status) === 1 ? 1 : 0,
        destaque: Number(formData.destaque) === 1 ? 1 : 0,
        id_categoria: formData.id_categoria ?? null,
        id_subcategoria: formData.id_subcategoria ?? null,

        imagem: cover?.url || null,
        galeria: galeriaFinal,

        parcelamento: {
          sem_juros: {
            habilitado: Number(formData.parcelamento_semjuros_enabled) === 1 ? 1 : 0,
            max_parcelas: num(formData.parcelamento_semjuros_qtd),
          },
          com_juros: {
            habilitado: Number(formData.parcelamento_comjuros_enabled) === 1 ? 1 : 0,
            max_parcelas: num(formData.parcelamento_comjuros_qtd),
            taxa_percentual_ao_mes: num(formData.parcelamento_comjuros_taxa_mes),
          },
        },

        descricao: {
          texto: formData.detalhes || "",
          caracteristicas: (formData.caracteristicas || []).filter((s) => String(s || "").trim() !== ""),
          ficha_tecnica: (formData.ficha_tecnica || []).filter(
            (p) =>
              String(p?.chave || "").trim() !== "" ||
              String(p?.valor || "").trim() !== ""
          ),
        },

        inclusos_dica: formData.inclusos_dica || "",
        inclusos: {
          itens: (formData.inclusos_itens || []).filter((s) => String(s || "").trim() !== ""),
        },

        compat_alerta: formData.compat_alerta || "",
        compatibilidade: {
          aplicacoes: compatAplicacoes,
        },

        garantia_alerta: formData.garantia_alerta || "",
        garantia_descricao: formData.garantia_descricao || "",
        garantia_acoes: (formData.garantia_acoes || []).map((s) => String(s || "").trim()).filter(Boolean),
        garantia_exclusoes: (formData.garantia_exclusoes || []).map((s) => String(s || "").trim()).filter(Boolean),
        garantia_policy_url: (formData.garantia_policy_url || "").trim() || null,

        /* Trocas & Devoluções */
        trocas_alerta: formData.trocas_alerta || "",
        trocas_descricao: formData.trocas_descricao || "",
        trocas_col_esq: formData.trocas_col_esq || "",
        trocas_col_dir: formData.trocas_col_dir || "",
        trocas_itens: (formData.trocas_itens || []).map((s) => String(s || "").trim()).filter(Boolean),
        trocas_passos: (formData.trocas_passos || []).map((s) => String(s || "").trim()).filter(Boolean),

        pagamento_regras: formData.pagamento_regras || "",

        aplicacao: (formData.aplicacao || [])
          .map((s) => String(s || "").trim())
          .filter((s) => s !== ""),

        // Cross-sell manual: envia lista de SKUs relacionados
        related_skus: String(formData.related_skus || "")
          .split(/[,;\n]/g)
          .map((s) => s.trim())
          .filter((s) => s !== ""),
      };

      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(
        err?.message || t("form.errorS3Upload")
      );
    } finally {
      setIsUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  const categoriesAlert = categoriesError
    ? (categoriesErrorObj?.message || t("form.errorLoadCategories"))
    : null;

  const companiesAlert = companiesError
    ? (companiesErrorObj?.message || t("form.errorLoadCompanies"))
    : null;

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={3}
      sx={{
        p: 2,
        width: "95%",
        m: "0 auto",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
            : "linear-gradient(180deg, #fff, #fafafa)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="h6">
          {product?.id ? t("form.titleEdit") : t("form.titleAdd")}
        </Typography>
        <Tooltip
          title={t("form.dealerPriceTooltip")}
        >
          <InfoOutlinedIcon sx={{ ml: 1, fontSize: 18, color: "text.secondary" }} />
        </Tooltip>
      </Box>

      {(errorMessage || categoriesAlert || companiesAlert) && (
        <Box sx={{ mb: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {categoriesAlert && <Alert severity="warning">{categoriesAlert}</Alert>}
          {companiesAlert && <Alert severity="warning">{companiesAlert}</Alert>}
        </Box>
      )}

      <ContentSection
        /* PROJETO */
        companies={companies}
        companiesLoading={companiesLoading}
        formData={formData}
        handleProjectChange={handleProjectChange}
        /* CATEGORIAS */
        categories={rootCategories}
        categoriesLoading={categoriesLoading}
        subcats={subcats}
        handleText={handleText}
        handleCategoryChange={(e, option) =>
          setFormData((p) => ({
            ...p,
            id_categoria: option ? option.id : null,
            id_subcategoria: null,
          }))
        }
        handleSubcatChange={(e, option) =>
          setFormData((p) => ({
            ...p,
            id_subcategoria: option ? option.id : null,
          }))
        }
      />

      <PriceStockSection
        formData={formData}
        handleText={handleText}
        precoBase={precoBase}
        precoFinal={precoFinal}
        economia={economia}
        economiaPct={economiaPct}
        descontoValido={precoConcessionarioValido}
      />

      <RelatedProductsSection
        formData={formData}
        handleText={handleText}
        currentProductId={product?.id}
      />

      <InstallmentsSection
        formData={formData}
        handleText={handleText}
        handleSwitch={handleSwitch}
        parcelamentoValido={
          (Number(formData.parcelamento_semjuros_enabled) !== 1 ||
            (Number(formData.parcelamento_semjuros_qtd) > 0 &&
              Number.isFinite(Number(formData.parcelamento_semjuros_qtd)))) &&
          (Number(formData.parcelamento_comjuros_enabled) !== 1 ||
            (Number(formData.parcelamento_comjuros_qtd) > 0 &&
              Number(formData.parcelamento_comjuros_taxa_mes) >= 0))
        }
      />

      <DescriptionTechSection
        formData={formData}
        handleText={handleText}
        setArrayItem={(f, i, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr[i] = v;
            return { ...prev, [f]: arr };
          })
        }
        addArrayItem={(f, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.push(v);
            return { ...prev, [f]: arr };
          })
        }
        removeArrayItem={(f, i) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.splice(i, 1);
            return { ...prev, [f]: arr };
          })
        }
      />

      <IncludedSection
        formData={formData}
        setArrayItem={(f, i, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr[i] = v;
            return { ...prev, [f]: arr };
          })
        }
        addArrayItem={(f, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.push(v);
            return { ...prev, [f]: arr };
          })
        }
        removeArrayItem={(f, i) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.splice(i, 1);
            return { ...prev, [f]: arr };
          })
        }
        handleText={handleText}
      />

      <ApplicationSection
        appsLabels={appsLabels}
        appsLoading={appsLoading}
        formData={formData}
        setFormData={setFormData}
      />

      {/* Compatibilidade por aplicação */}
      <CompatibilitySection
        appsLabels={appsLabels}
        appsLoading={appsLoading}
        formData={formData}
        setFormData={setFormData}
        handleText={handleText}
      />

      <WarrantySection
        formData={formData}
        handleText={handleText}
        setArrayItem={(f, i, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr[i] = v;
            return { ...prev, [f]: arr };
          })
        }
        addArrayItem={(f, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.push(v);
            return { ...prev, [f]: arr };
          })
        }
        removeArrayItem={(f, i) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.splice(i, 1);
            return { ...prev, [f]: arr };
          })
        }
      />

      <ReturnsSection
        formData={formData}
        handleText={handleText}
        setArrayItem={(f, i, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr[i] = v;
            return { ...prev, [f]: arr };
          })
        }
        addArrayItem={(f, v) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.push(v);
            return { ...prev, [f]: arr };
          })
        }
        removeArrayItem={(f, i) =>
          setFormData((prev) => {
            const arr = Array.isArray(prev[f]) ? [...prev[f]] : [];
            arr.splice(i, 1);
            return { ...prev, [f]: arr };
          })
        }
      />

      <PaymentSection formData={formData} handleText={handleText} />

      <GallerySection
        formData={formData}
        fileInputRef={fileInputRef}
        handleClickAdd={handleClickAdd}
        handleDrop={handleDrop}
        handleDragOver={handleDragOver}
        moveImage={moveImage}
        makeCover={makeCover}
        removeImage={removeImage}
      />

      <StatusHighlightSection formData={formData} handleSwitch={handleSwitch} />

      <ActionBar
        onClose={onClose}
        isFetching={isFetching || isUploading}
        disabled={
          isFetching ||
          isUploading ||
          !formData.ids_projetos || // obrigatório selecionar projeto
          !formData.nome ||
          formData.preco === "" ||
          formData.estoque === "" ||
          !precoConcessionarioValido ||
          !(
            (Number(formData.parcelamento_semjuros_enabled) !== 1 ||
              (Number(formData.parcelamento_semjuros_qtd) > 0 &&
                Number.isFinite(Number(formData.parcelamento_semjuros_qtd)))) &&
            (Number(formData.parcelamento_comjuros_enabled) !== 1 ||
              (Number(formData.parcelamento_comjuros_qtd) > 0 &&
                Number(formData.parcelamento_comjuros_taxa_mes) >= 0))
          )
        }
        statusHint={
          isUploading && uploadProgress.total
            ? t("form.uploadProgress", {
                done: uploadProgress.done,
                total: uploadProgress.total,
              })
            : undefined
        }
      />
    </Paper>
  );
}
